import {
  differenceInCalendarDays,
  format,
  formatDistanceToNowStrict,
  isThisWeek,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
} from 'date-fns';

export function toDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value;
}

/** 24-hour clock, matching UK timetable convention. */
export function formatTime(value: string | Date): string {
  return format(toDate(value), 'HH:mm');
}

export function formatDate(value: string | Date): string {
  return format(toDate(value), 'd MMM yyyy');
}

export function formatDateTime(value: string | Date): string {
  return format(toDate(value), 'd MMM yyyy, HH:mm');
}

export function formatWeekday(value: string | Date): string {
  return format(toDate(value), 'EEEE d MMMM');
}

/** "Today", "Tomorrow", "Friday", then a date once it is further out. */
export function formatRelativeDay(value: string | Date): string {
  const date = toDate(value);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  if (isYesterday(date)) return 'Yesterday';
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, 'EEEE');
  return format(date, 'd MMM');
}

export function formatRelativeDayTime(value: string | Date): string {
  return `${formatRelativeDay(value)}, ${formatTime(value)}`;
}

export function formatAgo(value: string | Date): string {
  return `${formatDistanceToNowStrict(toDate(value))} ago`;
}

/** Due-date phrasing that leads with urgency: "Overdue by 2 days", "Due today". */
export function formatDue(value: string | Date): { label: string; tone: 'danger' | 'warning' | 'neutral' } {
  const date = toDate(value);
  const days = differenceInCalendarDays(date, new Date());

  if (days < 0) {
    const magnitude = Math.abs(days);
    return { label: `Overdue by ${magnitude} day${magnitude === 1 ? '' : 's'}`, tone: 'danger' };
  }
  if (days === 0) return { label: 'Due today', tone: 'warning' };
  if (days === 1) return { label: 'Due tomorrow', tone: 'warning' };
  if (days <= 6) return { label: `Due ${format(date, 'EEEE')}`, tone: 'neutral' };
  return { label: `Due ${format(date, 'd MMM')}`, tone: 'neutral' };
}

export function formatDuration(startIso: string, endIso: string): string {
  const minutes = Math.round((toDate(endIso).getTime() - toDate(startIso).getTime()) / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function formatScore(score: number | null, maxScore: number | null): string {
  if (score === null) return '—';
  return maxScore !== null ? `${score}/${maxScore}` : String(score);
}

/** Title Case from an UPPER_SNAKE enum value, for display. */
export function humaniseEnum(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
